import {makeAutoObservable, observable} from 'mobx';
import CoursesRepository from '../../../../../data/repositories/CoursesRepository';
import type {CourseCategory} from '../../../types';
import type {CategoryStatus} from '../createOptions';

/**
 * The course categories the create page offers (those of the platform's New Course form, read
 * with its repository), and the one the author chose. A refresh, a failure or an empty list
 * never erases that choice.
 */
class CourseCategoriesStore {
  private repository = new CoursesRepository();
  private request = 0;

  /** The workspace whose categories are offered; outside one there are none. */
  public organizationId: string | undefined = undefined;
  public categories: CourseCategory[] = [];
  public category: CourseCategory | null = null;
  public status: CategoryStatus = 'loading';

  constructor() {
    makeAutoObservable<CourseCategoriesStore, 'repository' | 'request'>(
      this,
      {
        repository: false,
        request: false,
        categories: observable.ref,
        category: observable.ref,
      },
      {autoBind: true},
    );
  }

  /** Categories can be read: the page is inside a workspace. */
  public get available() {
    return !!this.organizationId;
  }

  /** Reads the workspace's categories; outside a workspace there are none to offer. */
  public load(organizationId?: string) {
    this.organizationId = organizationId;
    const request = ++this.request;
    if (!organizationId) {
      this.status = 'ready';
      return;
    }
    this.status = 'loading';
    this.repository.getLearingStandarts(organizationId).then(
      result => {
        if (request !== this.request) return;
        // A failed request is never shown as an empty list.
        if (result.success && result.data) this.loaded(result.data);
        else this.failed();
      },
      () => {
        if (request === this.request) this.failed();
      },
    );
  }

  public retry() {
    this.load(this.organizationId);
  }

  /** Drops the answer of a request still under way. */
  public stop() {
    this.request++;
  }

  public loaded(categories: CourseCategory[]) {
    this.categories = categories;
    this.status = 'ready';
  }

  public failed() {
    this.status = 'error';
  }

  /** Chooses a category by id; '' is no category. */
  public choose(id: string) {
    this.category =
      this.categories.find(item => item.id === id) ??
      (this.category?.id === id ? this.category : null);
  }

  /** The dropdown's options, with the chosen category even when a refresh lost it. */
  public get options() {
    const {category, categories} = this;
    return [
      ...categories.map(item => ({key: item.id, value: item.title})),
      ...(category && !categories.some(item => item.id === category.id)
        ? [{key: category.id, value: category.title}]
        : []),
    ];
  }

  public reset() {
    this.stop();
    this.organizationId = undefined;
    this.categories = [];
    this.category = null;
    this.status = 'loading';
  }
}

export default CourseCategoriesStore;
